// src/padplanner/paperGrid.js
//
// Paper view's grid, as numbers: which spacings show at a zoom, and where
// the dots and lines are. No map and no WebGL here (paperGridLayer.js draws
// it), so a test can hold the grid against proj4 and against the editor's
// snapping, and S7's report can ask for the same grid.
//
// ── THE GRID IS THE EDITOR'S ───────────────────────────────────────────────
//
// It is drawn in the authoring frame (frame.js: the revision's local_epsg, a
// WGS 84 UTM zone), not in screen or lng/lat axes. A dot is at E = i·g,
// N = j·g in that frame, with g the step in meters, computed as the editor's
// snapPointToGrid() computes a snapped point (Math.round(v / g) * g). So
// every anchor the editor snaps lands on a dot, to the last bit. Grid north
// is the frame's N axis: in the Permian (zone 13N, central meridian 105° W)
// it tilts about 1.3° from true north, and the lines tilt with it on screen.
//
// ALIGNED TO THE PAD (padAxis.js), the frame passed in is axisFrame()'s: its
// local axes are the pad's, about the perimeter's centroid, so the dots are
// whole steps in pad meters, where the editor's snapPointToGrid(…, axis)
// puts an anchor, through the same fromAxis.
//
// ── WHAT SHOWS AT A ZOOM (TIERS) ───────────────────────────────────────────
//
// Decided once per whole zoom level (the layer caches per level), at that
// level's scale, so within a level the spacing on screen only grows:
//
//   dots   at the snap step, when the step is at least DOT_MIN_PX apart
//   lines  the two finest of LINE_LADDER_FT that are at least LINE_MIN_PX
//          apart: the finer one light (minor), the coarser one heavy (major)
//
// At 32° N (512 px tiles) that gives, for the 5 ft step (paperGrid.test.js
// holds each level):
//
//   zoom 19 and up   5 ft dots, 50 ft and 100 ft lines
//   16–18            50 ft and 100 ft lines
//   15               100 and 500
//   13–14            500 and 1,000
//   12               1,000 and 5,000
//   10–11            5,000 alone
//   below 10         nothing
import { ftToM } from "../lib/units.js";

export const DOT_MIN_PX = 8;
export const LINE_MIN_PX = 12;
export const LINE_LADDER_FT = Object.freeze([50, 100, 500, 1000, 5000]);
// A bound on one cache's work, whatever the inputs: the dots the cached area can hold.
export const MAX_DOTS = 80000;
const SEGMENTS = 16;                       // a grid line, densified: UTM lines are curves in Web Mercator
const EARTH_CIRCUMFERENCE_M = 40075016.686;
const TILE_PX = 512;

/** Meters per CSS pixel at a zoom and latitude (MapLibre's 512 px tiles). */
export function metersPerPixel(zoom, lat) {
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (TILE_PX * 2 ** zoom);
}

/**
 * What the grid shows at a scale.
 * @returns {{dotsFt: number|null, minorFt: number|null, majorFt: number|null}}
 */
export function gridTier({ stepFt, mPerPx }) {
  const px = (ft) => ftToM(ft) / mPerPx;
  const dotsFt = stepFt > 0 && px(stepFt) >= DOT_MIN_PX ? stepFt : null;
  const lines = LINE_LADDER_FT.filter((ft) => ft > (dotsFt ?? 0) && px(ft) >= LINE_MIN_PX);
  return { dotsFt, minorFt: lines[0] ?? null, majorFt: lines[1] ?? null };
}

/** The legend: "grid 5 ft · major 50 ft · grid north" ("· pad axis 23.0°" when aligned); null when nothing is drawn. */
export function legendText(tier, axisDeg = null) {
  if (!tier) return null;
  const grid = tier.dotsFt ?? tier.minorFt;
  const major = tier.dotsFt ? tier.minorFt : tier.majorFt;
  if (grid == null) return null;
  const n = (ft) => `${ft.toLocaleString("en-US")} ft`;
  const up = axisDeg == null ? "grid north" : `pad axis ${axisDeg.toFixed(1)}°`;
  return [`grid ${n(grid)}`, major != null && `major ${n(major)}`, up].filter(Boolean).join(" · ");
}

/** Grid north's angle from true north at a point, degrees, clockwise positive. */
export function gridNorthDeg(frame, [lng, lat]) {
  const [e, n] = frame.toLocal([lng, lat]);
  const up = frame.toLngLat([e, n + 100]);
  // Local east-north at the point, in meters: the angle of the frame's +N.
  const dx = (up[0] - lng) * Math.cos((lat * Math.PI) / 180);
  const dy = up[1] - lat;
  return (Math.atan2(dx, dy) * 180) / Math.PI;
}

/** The frame's extent over a lng/lat box: its corners and edge midpoints, in E, N. */
function localExtent(frame, [w, s, e, n]) {
  const pts = [[w, s], [e, s], [e, n], [w, n], [(w + e) / 2, s], [(w + e) / 2, n], [w, (s + n) / 2], [e, (s + n) / 2]];
  let minE = Infinity, minN = Infinity, maxE = -Infinity, maxN = -Infinity;
  for (const p of pts) {
    const [x, y] = frame.toLocal(p);
    minE = Math.min(minE, x); maxE = Math.max(maxE, x); minN = Math.min(minN, y); maxN = Math.max(maxN, y);
  }
  return { minE, minN, maxE, maxN };
}

const range = (lo, hi, g) => {
  const out = [];
  for (let k = Math.ceil(lo / g); k * g <= hi; k++) out.push(k * g);
  return out;
};

/**
 * The grid over a lng/lat box: dots as lng/lat, and lines as lng/lat
 * polylines (densified, since a UTM line is not straight in Web Mercator).
 * A spacing whose dots or lines would exceed MAX_DOTS is left out.
 * @param {{frame: object, bbox: number[], tier: object}} p
 * @returns {{dots: number[][], minor: number[][][], major: number[][][]}}
 */
export function gridGeometry({ frame, bbox, tier }) {
  const ext = localExtent(frame, bbox);
  const out = { dots: [], minor: [], major: [] };
  if (tier.dotsFt) {
    const g = ftToM(tier.dotsFt);
    const es = range(ext.minE, ext.maxE, g), ns = range(ext.minN, ext.maxN, g);
    if (es.length * ns.length <= MAX_DOTS) for (const e of es) for (const n of ns) out.dots.push(frame.toLngLat([e, n]));
  }
  const lines = (ft) => {
    if (!ft) return [];
    const g = ftToM(ft);
    const es = range(ext.minE, ext.maxE, g), ns = range(ext.minN, ext.maxN, g);
    if (es.length + ns.length > MAX_DOTS / SEGMENTS) return [];
    const along = (a, b) => Array.from({ length: SEGMENTS + 1 }, (_, i) => a + ((b - a) * i) / SEGMENTS);
    return [
      ...es.map((e) => along(ext.minN, ext.maxN).map((n) => frame.toLngLat([e, n]))),
      ...ns.map((n) => along(ext.minE, ext.maxE).map((e) => frame.toLngLat([e, n]))),
    ];
  };
  out.minor = lines(tier.minorFt);
  // A major line is also a minor one (100 is a multiple of 50): drawn once, heavy.
  out.major = lines(tier.majorFt);
  return out;
}

/** Web Mercator, MapLibre's world units (0..1 across), in doubles. */
export function mercator([lng, lat]) {
  const x = (lng + 180) / 360;
  const s = Math.sin((lat * Math.PI) / 180);
  const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  return [x, y];
}
